/**
 * <subscription-options> custom element.
 *
 * Port of Expanse's subscription UI into Horizon's component-based
 * architecture. Handles radio card selection, frequency dropdown,
 * live price updates, variant matching for bundle products, cart
 * submission with correct variant/selling_plan, and info modals.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {'onetime'|'subscription'|'additional_subscription'|'additional_onetime'|'additional_six_months_subscription'} SubOption */

/**
 * cadence-buybox: months divisor per subscription mode (card per-month
 * price = allocation ÷ months; ATC compare = main one-time price × months).
 * Hardcoded per-mode cadence — v3's ÷3 precedent. Keep in lockstep with
 * the Liquid ÷3 on the additional card in blocks/subscription-options.liquid.
 */
const CADENCE_MONTHS = {
  subscription: 1,
  additional_subscription: 3,
  additional_six_months_subscription: 6,
};

class SubscriptionOptions extends Component {
  /* ────────────────────────────────────────────────────────────────
   * Static identity (from block data-attributes)
   * ──────────────────────────────────────────────────────────────── */

  /** @type {string | null} */
  #sectionId = null;
  /** @type {string | null} */
  #productId = null;
  /** @type {string | null} */
  #productHandle = null;

  /** @type {'default'|'v1'|'v2'|'v2b'|'v3'|'v4'|'v5'|'new-buybox'|'cadence-buybox'} Pricing-box visual style (from data-pricing-box-style). */
  #pricingBoxStyle = 'default';

  /** @type {string} cadence per-month unit word (from the data-cadence-unit host attribute ← price_unit_label metafield; 'month' default). */
  #cadenceUnit = 'month';

  /**
   * @type {boolean} Per-cadence PRICING (÷ months + "/month" suffix on the card
   * prices), independent of the card styling. True for cadence-buybox, and for
   * any other style whose product sets `subscription.show_cadence_pricing` —
   * that is how HRA runs per-month prices inside its v5 cards. Read from the
   * PRESENCE of data-cadence-unit, which the Liquid emits on exactly the same
   * condition; keep the two in lockstep.
   *
   * A/B override: `window.__svCadencePricing` wins over the attribute when it
   * is a real boolean — see connectedCallback.
   */
  #cadenceActive = false;

  /* ────────────────────────────────────────────────────────────────
   * Parsed JSON payloads (from snippets/subscription-product-data.liquid)
   * ──────────────────────────────────────────────────────────────── */

  /** @type {any | null} */
  #mainProduct = null;
  /** @type {any | null} */
  #additionalProduct = null;
  /** @type {any | null} */
  #sixMonthsProduct = null;
  /** @type {any | null} */
  #context = null;

  /**
   * @type {{ base_price: number | null, variants: Record<string, { boxes: number, recommended: number | null }> } | null}
   * `new_buybox` payload from the context JSON. Non-null only when the
   * product's pricing_box_style is 'new-buybox' (the snippet gates the
   * emission), so a null check doubles as the style gate.
   */
  #nb = null;

  /* ────────────────────────────────────────────────────────────────
   * Cached DOM references
   * ──────────────────────────────────────────────────────────────── */

  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;
  /** @type {HTMLSelectElement | null} */
  #masterSellingPlanSelect = null;
  /** @type {HTMLSelectElement | null} */
  #frequenciesSelect = null;
  /** @type {HTMLElement | null} */
  #frequenciesSelectWrapper = null;

  /* ────────────────────────────────────────────────────────────────
   * Runtime state
   * ──────────────────────────────────────────────────────────────── */

  /** @type {SubOption | null} */
  #selectedOption = null;

  /** @type {number | null} Selling plan id currently reflected in hidden input + URL. */
  #selectedSellingPlanId = null;

  /** @type {number | null} Main product's current variant id. */
  #currentMainVariantId = null;

  /** @type {boolean} new-buybox two-product switcher active (this product links a separate additional product). */
  #twoProducts = false;
  /** @type {'main'|'additional'} Active product tile in two-product mode. */
  #activeBuyboxProduct = 'main';

  /** @type {number | null} URL-param selling plan, kept until validated against an allocation. */
  #urlSellingPlanId = null;

  /** @type {HTMLElement | null} Cached quantity-selector-component ref (may be null on products without quantity UI). */
  #quantitySelector = null;

  /** @type {{variantUpdate: (e:Event)=>void, keydown: (e:KeyboardEvent)=>void, modalClick: (e:MouseEvent)=>void, tileChange: (e:Event)=>void} | null} */
  #handlers = null;

  /** @type {((e: SubmitEvent) => void) | null} */
  #submitHandler = null;

  /** @type {((e: Event) => void) | null} Bound buy-once click handler (v5 only). */
  #buyOnceClickHandler = null;

  /* ────────────────────────────────────────────────────────────────
   * Lifecycle
   * ──────────────────────────────────────────────────────────────── */

  connectedCallback() {
    super.connectedCallback();

    this.#sectionId = this.dataset.sectionId ?? null;
    this.#productId = this.dataset.productId ?? null;
    this.#productHandle = this.dataset.productHandle ?? null;
    this.#pricingBoxStyle = /** @type {'default'|'v1'|'v2'|'v2b'|'v3'|'v4'|'v5'|'new-buybox'|'cadence-buybox'} */ (
      this.dataset.pricingBoxStyle ?? 'default'
    );
    // Per-visitor A/B override for split testing (Convert et al). The server
    // renders the control arm, so crawlers and no-JS visitors always get the
    // metafield's answer; an experiment sets `window.__svCadencePricing` from a
    // SYNCHRONOUS <head> snippet — i.e. before this element upgrades — and this
    // read happens ahead of the #updatePrices() call below that repaints every
    // card. Because the decision lives on the instance rather than in the DOM,
    // it also survives the five other repaint paths (radio, frequency, tile,
    // variant, morph); a DOM-editing experiment would be wiped by the first
    // plan click.
    //
    // Boolean ONLY, deliberately: `true` forces cadence pricing, `false` forces
    // the control, anything else (including the string "true") is ignored and
    // falls through to the attribute. A revenue-critical display should not be
    // flippable by a stray truthy value.
    const cadenceOverride = window.__svCadencePricing;
    this.#cadenceActive = typeof cadenceOverride === 'boolean'
      ? cadenceOverride
      : this.hasAttribute('data-cadence-unit');
    this.#cadenceUnit = this.dataset.cadenceUnit || 'month';

    this.#parseJsonPayloads();
    this.#cacheDomRefs();
    this.#ensureSellingPlanInput();
    this.#bindSubmitListener();
    this.#seedInitialState();
    this.#bindEventListeners();

    // Apply seeded state to the DOM: rebuild visible frequency
    // options for the seeded option, write the hidden input value,
    // align the URL param, and paint all 4 cards' prices.
    // Subsequent radio/dropdown changes flow through the same
    // methods.
    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
    this.#applyQuantityVisibility();
    this.#applyQuantityLabel();
    this.#applyVariantModalBody();
    this.#applyVariantIdOverride();
    this.#portalModals();
    this.#initBuyOnce();

  }

  updatedCallback() {
    super.updatedCallback();
    this.#cacheDomRefs();
    this.#ensureSellingPlanInput();
    this.#bindSubmitListener();
    // Re-assert our state into the morphed DOM. Without this, a
    // buy-buttons morph after variant:update would lose our hidden
    // input value even if the input itself was re-created, and the
    // card prices would fall back to whatever the server re-rendered.
    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
    this.#applyQuantityVisibility();
    this.#applyQuantityLabel();
    this.#applyVariantModalBody();
    this.#applyVariantIdOverride();
    this.#portalModals();
    this.#initBuyOnce();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#unbindEventListeners();
    // Remove portalled modals when the component leaves the DOM.
    if (this.#sectionId) {
      document
        .querySelectorAll(`[data-subscription-popup][data-portal-for="${this.#sectionId}"]`)
        .forEach((m) => m.remove());
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Setup helpers
   * ──────────────────────────────────────────────────────────────── */

  #parseJsonPayloads() {
    this.#mainProduct = this.#readJson('[data-subscription-main-product]');
    this.#additionalProduct = this.#readJson('[data-subscription-additional-product]');
    this.#sixMonthsProduct = this.#readJson('[data-subscription-six-months-product]');
    this.#context = this.#readJson('[data-subscription-context]');
    this.#nb = this.#context?.new_buybox ?? null;
    // Two-product switcher is active only when the new-buybox context flags it
    // AND the additional product JSON is present (dual gate; mirrors the Liquid
    // nb_two_products). Otherwise every two-product branch below no-ops.
    this.#twoProducts = !!(this.#nb && this.#nb.two_products && this.#additionalProduct);

    if (!this.#mainProduct) {
      // eslint-disable-next-line no-console
      console.warn('[subscription-options] main product JSON missing');
    }
  }

  /**
   * @param {string} selector
   * @returns {any | null}
   */
  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent ?? 'null');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[subscription-options] failed to parse ${selector}`, err);
      return null;
    }
  }

  #cacheDomRefs() {
    this.#frequenciesSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-variant-selling-plan-select]')
    );
    this.#frequenciesSelectWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-variant-selling-plan-select-wrapper]')
    );
    this.#masterSellingPlanSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-master-selling-plan-select]')
    );

    const productDetails = this.closest('.product-details') ?? document;
    const pfc = productDetails.querySelector('product-form-component');
    this.#form = /** @type {HTMLFormElement | null} */ (
      pfc?.querySelector('form[data-type="add-to-cart-form"]') ?? null
    );
    this.#sellingPlanInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="selling_plan"][data-subscription-injected]') ?? null
    );
  }

  #ensureSellingPlanInput() {
    if (!this.#form) return;
    if (this.#sellingPlanInput && this.#form.contains(this.#sellingPlanInput)) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = '';
    input.setAttribute('data-subscription-injected', '');
    this.#form.appendChild(input);
    this.#sellingPlanInput = input;
  }

  /**
   * Seed runtime state from the server-rendered DOM:
   *   - #selectedOption from the checked radio (server preselect).
   *   - #currentMainVariantId from <form><input name="id">.
   *   - #urlSellingPlanId from ?selling_plan= (held until validated).
   *
   * Cross-mode URL restoration (3e fix): if the URL plan exists but
   * belongs to a mode different from the server preselect, override
   * #selectedOption to the URL plan's owning mode AND tick that radio
   * BEFORE the orchestrator runs. Without this override the orchestrator
   * would search the preselected mode's allocations, fail to find the
   * URL plan, fall back to that mode's first plan, and silently rewrite
   * the URL via #updateSellingPlanParam — destroying the deep-link.
   */
  #seedInitialState() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-subscription-option-control]:checked')
    );
    if (checked) {
      this.#selectedOption = /** @type {SubOption} */ (checked.value);
    }

    // Two-product switcher: seed the active product tile (server preselect).
    if (this.#twoProducts) {
      const tile = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-buybox-product-tile]:checked')
      );
      this.#activeBuyboxProduct = /** @type {'main'|'additional'} */ (
        (tile && tile.dataset.buyboxProductTile) || 'main'
      );
    }

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="id"]') ?? null
    );
    const vid = Number(variantInput?.value ?? NaN);
    this.#currentMainVariantId = Number.isFinite(vid) ? vid : null;

    const params = new URLSearchParams(window.location.search);
    const raw = params.get('selling_plan');
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) this.#urlSellingPlanId = n;
    }

    // Cross-mode URL plan restoration.
    if (this.#urlSellingPlanId != null) {
      const ownerMode = this.#resolveModeForPlan(this.#urlSellingPlanId);
      if (ownerMode && ownerMode !== this.#selectedOption) {
        if (this.#twoProducts && ownerMode === 'additional_subscription') {
          // Two-product: the additional product is the TILE, not a radio.
          // Restore by activating the additional tile + the subscription radio.
          this.#activeBuyboxProduct = 'additional';
          this.#selectedOption = 'subscription';
          const tile = /** @type {HTMLInputElement | null} */ (
            this.querySelector('[data-buybox-product-tile="additional"]')
          );
          if (tile) tile.checked = true;
          const subRadio = /** @type {HTMLInputElement | null} */ (
            this.querySelector('[data-subscription-option-control][value="subscription"]')
          );
          if (subRadio) subRadio.checked = true;
        } else {
          this.#selectedOption = ownerMode;
          // Tick the right radio visually; setting `.checked = true` on
          // a radio in a name-group automatically unchecks its siblings.
          // We don't dispatch a `change` event because the orchestrator
          // about to run reads #selectedOption directly.
          const radio = /** @type {HTMLInputElement | null} */ (
            this.querySelector(
              `[data-subscription-option-control][value="${ownerMode}"]`
            )
          );
          if (radio) radio.checked = true;
        }
      }
    }
  }

  /**
   * Given a plan id, find which subscription mode owns it by scanning
   * each linked product's variants' allocations. Returns null when the
   * id doesn't match any allocation on any linked product (URL is stale,
   * pasted from a different product, or the plan was retired in Recharge).
   *
   * Plans in Shopify are scoped to selling-plan groups attached to
   * specific products, so a given plan id appears on at most one of
   * (main / 3-mo / 6-mo). We pick the first match without ambiguity.
   *
   * @param {number} planId
   * @returns {SubOption | null}
   */
  #resolveModeForPlan(planId) {
    /** @type {Array<[SubOption, any | null]>} */
    const pairs = [
      ['subscription', this.#mainProduct],
      ['additional_subscription', this.#additionalProduct],
      ['additional_six_months_subscription', this.#sixMonthsProduct],
    ];
    for (const [mode, product] of pairs) {
      if (!product) continue;
      for (const variant of product.variants ?? []) {
        for (const alloc of variant.selling_plan_allocations ?? []) {
          if (Number(alloc?.selling_plan_id) === planId) return mode;
        }
      }
    }
    return null;
  }

  /* ────────────────────────────────────────────────────────────────
   * Event wiring
   * ──────────────────────────────────────────────────────────────── */

  #bindEventListeners() {
    if (this.#handlers) return;

    this.addEventListener('change', (e) => {
      const t = /** @type {HTMLElement | null} */ (e.target);
      if (!t) return;
      if (t.matches('[data-subscription-option-control]')) {
        this.#onRadioChange(/** @type {HTMLInputElement} */ (t));
      } else if (t.matches('[data-buybox-product-tile]')) {
        this.#onBuyboxTileChange(/** @type {HTMLInputElement} */ (t));
      } else if (t.matches('[data-variant-selling-plan-select]')) {
        this.#onFrequencyChange(/** @type {HTMLSelectElement} */ (t));
      }
    });

    // Delegated click handler for info-modal triggers.
    // The trigger lives inside <label>, so we MUST preventDefault
    // and stopPropagation — otherwise clicking the ⓘ button also
    // flips the radio that the label controls.
    this.addEventListener('click', (e) => {
      const t = /** @type {Element | null} */ (e.target);
      if (!(t instanceof Element)) return;
      const trigger = t.closest('[data-subscription-info-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const id = trigger.getAttribute('data-subscription-info-trigger');
        if (id) this.#openModal(id);
      }
    });

    // Document-level click handler for close buttons / overlay clicks
    // inside the portalled modals (which now live in <body> and so won't
    // bubble up to `this`).
    const sectionId = this.#sectionId ?? '';
    const modalClick = (/** @type {MouseEvent} */ e) => {
      const t = /** @type {Element | null} */ (e.target);
      if (!(t instanceof Element)) return;
      const close = t.closest('[data-subscription-popup-close]');
      if (!close) return;
      const modal = close.closest(
        `[data-subscription-popup][data-portal-for="${sectionId}"]`
      );
      if (modal) this.#closeModal(modal);
    };
    document.addEventListener('click', modalClick);

    const variantUpdate = (/** @type {Event} */ e) => this.#onVariantUpdate(e);
    document.addEventListener(ThemeEvents.variantUpdate, variantUpdate);

    // Document-level Escape: closes any open modal in this block.
    // Document-scoped so it fires regardless of focus location (e.g.
    // when the user has tabbed into the modal's container).
    const keydown = (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'Escape') this.#closeAllOpenModals();
    };
    document.addEventListener('keydown', keydown);

    // new-buybox optimistic variant update. The native <variant-picker>
    // (variant-tiles block) fetches the FULL product page before
    // dispatching ThemeEvents.variantUpdate — a visible delay before
    // the buy box re-prices. All variant pricing already lives in our
    // parsed product JSON, so react to the tile radio's change event
    // immediately; the gallery / URL / morph still follow when the
    // fetch lands, and #onVariantUpdate re-runs the same updaters
    // idempotently with the authoritative payload. Document-level
    // because the tiles block is a sibling, not a descendant.
    const tileChange = (/** @type {Event} */ e) => {
      if (!this.#nb) return;
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const picker = input.closest('variant-picker');
      if (!picker || String(picker.dataset.productId ?? '') !== String(this.#productId)) return;
      const vid = Number(input.dataset.variantId);
      if (!Number.isFinite(vid) || vid === this.#currentMainVariantId) return;
      this.#currentMainVariantId = vid;
      this.#updateMasterFrequencySelect();
      this.#updateSellingPlanSelectValue();
      this.#updatePrices();
      this.#applyAtcPrice();
      this.#applyQuantityVisibility();
      this.#applyQuantityLabel();
      this.#applyVariantModalBody();
      this.#applyVariantIdOverride();
    };
    document.addEventListener('change', tileChange);

    this.#handlers = { variantUpdate, keydown, modalClick, tileChange };
  }

  #unbindEventListeners() {
    if (this.#handlers) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#handlers.variantUpdate);
      document.removeEventListener('keydown', this.#handlers.keydown);
      document.removeEventListener('click', this.#handlers.modalClick);
      document.removeEventListener('change', this.#handlers.tileChange);
      this.#handlers = null;
    }
    this.#unbindSubmitListener();
  }

  /* ────────────────────────────────────────────────────────────────
   * Handlers
   * ──────────────────────────────────────────────────────────────── */

  /** @param {HTMLInputElement} input */
  #onRadioChange(input) {
    const next = /** @type {SubOption} */ (input.value);
    if (next === this.#selectedOption) return;
    this.#selectedOption = next;

    // When switching INTO a subscription mode, clear any stale
    // selected plan — #updateMasterFrequencySelect will pick the
    // correct first/URL-preferred plan for the new mode.
    this.#selectedSellingPlanId = null;

    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
    this.#applyQuantityVisibility();
    this.#applyQuantityLabel();
    this.#applyVariantModalBody();
    this.#applyVariantIdOverride();
  }

  /** @param {HTMLSelectElement} select */
  #onFrequencyChange(select) {
    const opt = select.selectedOptions[0];
    if (!opt) return;
    const planId = Number(opt.dataset.sellingPlanId);
    if (!Number.isFinite(planId)) return;
    this.#selectedSellingPlanId = planId;
    // Once the user explicitly picks, drop any URL-seeded plan so
    // subsequent rebuilds don't re-snap back.
    this.#urlSellingPlanId = null;

    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
  }

  /**
   * Two-product switcher tile change (main | additional). The shared
   * Subscribe & Save + One-Time cards stay; we just swap which product
   * they apply to, then re-run the full orchestrator chain so frequency
   * options, prices, labels, modal copy, and the cart variant id all
   * follow the active product.
   * @param {HTMLInputElement} input
   */
  #onBuyboxTileChange(input) {
    if (!input.checked) return;
    const next = /** @type {'main'|'additional'} */ (input.dataset.buyboxProductTile || 'main');
    if (next === this.#activeBuyboxProduct) return;
    this.#activeBuyboxProduct = next;
    // Reset the plan so #updateMasterFrequencySelect picks the active
    // product's recommended/first cadence.
    this.#selectedSellingPlanId = null;
    this.#urlSellingPlanId = null;

    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
    this.#applyQuantityVisibility();
    this.#applyQuantityLabel();
    this.#applyVariantModalBody();
    this.#applyVariantIdOverride();
  }

  /**
   * Fold (active product tile) × (subscription/onetime radio) into the
   * Horizon mode string the rest of the component understands. Returns
   * the raw #selectedOption when the two-product switcher is inactive,
   * so every legacy / single-product / bundle-card path is unchanged.
   * @returns {SubOption | null}
   */
  #effectiveMode() {
    if (!this.#twoProducts) return this.#selectedOption;
    const base = this.#selectedOption === 'onetime' ? 'onetime' : 'subscription';
    if (this.#activeBuyboxProduct === 'additional') {
      return base === 'onetime' ? 'additional_onetime' : 'additional_subscription';
    }
    return /** @type {SubOption} */ (base);
  }

  /**
   * The variant whose price/labels are currently in play: the main
   * variant for the main tile, the matched additional bundle variant
   * for the additional tile.
   * @returns {any | null}
   */
  #activeVariant() {
    if (this.#twoProducts && this.#activeBuyboxProduct === 'additional') {
      return this.#targetVariantFor('additional_subscription') ?? this.#mainVariant();
    }
    return this.#mainVariant();
  }

  /** @returns {number | null} */
  #activeVariantId() {
    const v = this.#activeVariant();
    return v ? Number(v.id) : this.#currentMainVariantId;
  }

  /**
   * React to variant changes on the MAIN product. Same-product variant
   * swaps morph only the variant-picker + product-form-buttons subtree;
   * our block is a sibling and stays intact, so updatedCallback does
   * not fire. We own the full rehydration path here:
   *
   *   1. Filter to events for our product (ignore other PDPs / combined-
   *      listing navigations that target a different product id — those
   *      trigger a `<main>` morph that re-mounts this element anyway).
   *   2. Update #currentMainVariantId from event.detail.resource.id,
   *      with a form-input fallback.
   *   3. Re-acquire DOM refs + re-inject the hidden `selling_plan` input
   *      (buy-buttons morph wipes it along with `<input name="id">`).
   *   4. Re-run orchestrator chain → bundle cards re-match by option
   *      values against the new main variant; all 4 cards re-price.
   *
   * @param {Event} e
   */
  #onVariantUpdate(e) {
    const detail = /** @type {CustomEvent<any>} */ (e).detail;
    const eventProductId = String(detail?.data?.productId ?? '');
    if (!eventProductId || eventProductId !== String(this.#productId)) return;

    const newVid = Number(detail?.resource?.id);
    if (Number.isFinite(newVid)) {
      this.#currentMainVariantId = newVid;
    }
    // Note: we do NOT fall back to reading [name="id"] from the form here
    // because #applyVariantIdOverride may have written a bundle variant ID
    // into that input. The event detail is the authoritative source.

    this.#cacheDomRefs();
    this.#ensureSellingPlanInput();
    this.#bindSubmitListener();

    this.#updateMasterFrequencySelect();
    this.#updateSellingPlanSelectValue();
    this.#updatePrices();
    this.#applyAtcPrice();
    this.#applyQuantityVisibility();
    this.#applyQuantityLabel();
    this.#applyVariantModalBody();
    this.#applyVariantIdOverride();
  }

  /* ────────────────────────────────────────────────────────────────
   * Orchestrator: rebuilds visible frequency select for the current
   * mode, picks a selected plan, and updates internal state.
   *
   * After this runs, #selectedSellingPlanId points at the plan that
   * the hidden <input name="selling_plan"> + master select should
   * reflect. Actual DOM write happens in #updateSellingPlanSelectValue.
   * ──────────────────────────────────────────────────────────────── */
  #updateMasterFrequencySelect() {
    const mode = this.#effectiveMode();
    if (!mode) return;

    // Figure out which product + variant sources allocations for this mode.
    const sourceProduct = this.#sourceProductFor(mode);
    const targetVariant = this.#targetVariantFor(mode);
    const allocations = targetVariant?.selling_plan_allocations ?? [];

    // One-time (incl. additional_onetime) carries no plan — clear selection
    // and hide the wrapper even though the additional product's variant has
    // allocations (a one-time purchase of it must not attach a selling plan).
    if (mode === 'onetime' || mode === 'additional_onetime' || allocations.length === 0) {
      this.#selectedSellingPlanId = null;
      this.#setFrequencyWrapperHidden(true);
      // Keep visible <select> intact shape-wise but wipe its options
      // so stale entries don't confuse accessibility tools.
      if (this.#frequenciesSelect) this.#frequenciesSelect.innerHTML = '';
      return;
    }

    // Rebuild visible select options to match the allocations of the target variant.
    const planLookup = this.#buildPlanLookup(sourceProduct);
    this.#rebuildFrequencyOptions(targetVariant, allocations, planLookup);

    // Pick which plan is selected:
    //   1. URL param if it matches one of these allocations.
    //   2. Recommended position (new-buybox: per-variant metafield first;
    //      otherwise the product-level context value — see
    //      #nbRecommendedPosition).
    //   3. First allocation.
    let picked = null;

    if (this.#urlSellingPlanId) {
      const match = allocations.find(
        (a) => Number(a?.selling_plan_id) === this.#urlSellingPlanId
      );
      if (match) picked = Number(match.selling_plan_id);
    }

    if (picked == null) {
      const recommended = this.#nbRecommendedPosition(targetVariant);
      if (recommended <= allocations.length) {
        picked = Number(allocations[recommended - 1]?.selling_plan_id);
      }
    }

    if (picked == null && allocations[0]?.selling_plan_id != null) {
      picked = Number(allocations[0].selling_plan_id);
    }

    this.#selectedSellingPlanId = Number.isFinite(picked) ? picked : null;

    // Reflect the pick in the visible <select> so the user sees the
    // correct choice highlighted.
    if (this.#frequenciesSelect && this.#selectedSellingPlanId != null) {
      const val = String(this.#selectedSellingPlanId);
      for (const o of this.#frequenciesSelect.options) {
        if (o.value === val) {
          o.selected = true;
          break;
        }
      }
    }

    // Hide the wrapper when there's no meaningful choice.
    this.#setFrequencyWrapperHidden(allocations.length < 2);
  }

  /**
   * Resolve the variant whose selling_plan_allocations drive the
   * current mode.
   * @param {SubOption} mode
   * @returns {any | null}
   */
  #targetVariantFor(mode) {
    const src = this.#sourceProductFor(mode);
    if (!src) return null;

    if (mode === 'subscription') {
      return (
        src.variants.find((v) => Number(v.id) === this.#currentMainVariantId) ??
        src.variants[0] ??
        null
      );
    }

    // Bundles (3-mo / 6-mo): match the main variant's option1/2/3
    // case-insensitively against the bundle product's variants, falling
    // back to the first variant with allocations.
    const mainVariant = this.#mainProduct?.variants?.find(
      (v) => Number(v.id) === this.#currentMainVariantId
    ) ?? null;
    return this.#bundleVariantByOptions(src, mainVariant);
  }

  /**
   * Resolve a bundle product's variant by matching option1/option2/option3
   * case-insensitively against the main variant's option values. Ported
   * from Expanse's `findAdditionalProductVariant` / `findSixMonthSubscriptionVariant`
   * (theme.js 8254-8308). Falls back to the first variant with allocations,
   * then to the first variant.
   * @param {any | null} bundleProduct
   * @param {any | null} mainVariant
   * @returns {any | null}
   */
  #bundleVariantByOptions(bundleProduct, mainVariant) {
    const variants = bundleProduct?.variants ?? [];
    if (variants.length === 0) return null;

    const firstWithAllocs = () =>
      variants.find(
        (v) => Array.isArray(v.selling_plan_allocations) && v.selling_plan_allocations.length > 0
      ) ?? variants[0] ?? null;

    if (!mainVariant) return firstWithAllocs();

    const norm = (x) => (x == null ? '' : String(x).trim().toLowerCase());
    const o1 = norm(mainVariant.option1);
    const o2 = norm(mainVariant.option2);
    const o3 = norm(mainVariant.option3);

    // Match across all option positions that the main variant carries.
    // A bundle variant only needs to match the positions the main variant
    // defines (single-option products still match on option1 alone).
    const match = variants.find((v) => {
      if (o1 !== '' && norm(v.option1) !== o1) return false;
      if (o2 !== '' && norm(v.option2) !== o2) return false;
      if (o3 !== '' && norm(v.option3) !== o3) return false;
      return true;
    });

    return match ?? firstWithAllocs();
  }

  /**
   * Resolve the product object (main / 3-mo bundle / 6-mo bundle) that
   * owns the allocations + selling_plan_groups for a given mode.
   * @param {SubOption} mode
   * @returns {any | null}
   */
  #sourceProductFor(mode) {
    switch (mode) {
      case 'subscription':
        return this.#mainProduct;
      case 'additional_subscription':
      case 'additional_onetime':
        return this.#additionalProduct;
      case 'additional_six_months_subscription':
        return this.#sixMonthsProduct;
      case 'onetime':
      default:
        return null;
    }
  }

  /**
   * Build a Map<string plan_id, { id, name, group_id }> from a
   * product's selling_plan_groups. Allocations reference plans via
   * flat `selling_plan_id` / `selling_plan_group_id` (Horizon's native
   * `{{ product | json }}` schema); plan names/options live here.
   * @param {any | null} product
   * @returns {Map<string, { id:number, name:string, group_id:string }>}
   */
  #buildPlanLookup(product) {
    const map = new Map();
    const groups = product?.selling_plan_groups ?? [];
    for (const g of groups) {
      const gid = g?.id ?? null;
      for (const p of g?.selling_plans ?? []) {
        if (p?.id == null) continue;
        map.set(String(p.id), {
          id: Number(p.id),
          name: String(p.name ?? ''),
          group_id: gid != null ? String(gid) : '',
        });
      }
    }
    return map;
  }

  /* ────────────────────────────────────────────────────────────────
   * new-buybox helpers
   *
   * Both helpers are safe on every product: when #nb is null (any
   * style other than 'new-buybox') they reduce to the product-level
   * behavior the call sites had before.
   * ──────────────────────────────────────────────────────────────── */

  /**
   * Box count for a main-product variant (new-buybox per-box pricing
   * divisor, from the variant's `quantity_label` metafield via the
   * context payload). Min 1; returns 1 for unknown variants or when
   * new-buybox is inactive.
   * @param {number | string | null | undefined} variantId
   * @returns {number}
   */
  #nbBoxesFor(variantId) {
    const boxes = Number(this.#nb?.variants?.[String(variantId)]?.boxes);
    return Number.isFinite(boxes) && boxes >= 1 ? boxes : 1;
  }

  /**
   * Unit word ("bottle"/"box") for the per-unit price suffix, per variant.
   * Defaults to "bottle". new-buybox only.
   * @param {number | string | null | undefined} variantId
   * @returns {string}
   */
  #nbUnitFor(variantId) {
    const u = this.#nb?.variants?.[String(variantId)]?.unit;
    return typeof u === 'string' && u ? u : 'box';
  }

  /**
   * Whether the One-Time card shows the full price (no per-unit division /
   * suffix) for the given variant. new-buybox only; defaults false.
   * @param {number | string | null | undefined} variantId
   * @returns {boolean}
   */
  #nbOnetimeFullFor(variantId) {
    return this.#nb?.variants?.[String(variantId)]?.onetime_full === true;
  }

  /**
   * Whether the "(Recommended)" suffix should be shown in the frequency
   * dropdown for the given variant's product. Defaults true; suppressed
   * by the per-product hide_recommended_label metafield (the data-recommended
   * attribute — and thus auto-select — is unaffected).
   * @param {number | string | null | undefined} variantId
   * @returns {boolean}
   */
  #nbShowRecommendedFor(variantId) {
    return this.#nb?.variants?.[String(variantId)]?.hide_recommended !== true;
  }

  /**
   * Per-unit price in cents (price ÷ box-count). Two-product mode keeps
   * cents (per-bottle, Math.round); single-product new-buybox keeps the
   * original whole-dollar rounding so the existing box doesn't change.
   * @param {number} priceCents
   * @param {number | string | null | undefined} variantId
   * @returns {number}
   */
  #nbPerUnit(priceCents, variantId) {
    const boxes = this.#nbBoxesFor(variantId);
    if (this.#twoProducts) return Math.round(priceCents / boxes);
    return Math.round(priceCents / 100 / boxes) * 100;
  }

  /**
   * cadence-buybox unit suffix, e.g. '/month' (price_unit_label metafield
   * via the data-cadence-unit host attribute — the Liquid unit spans must
   * emit the same word; keep in lockstep).
   * @returns {string}
   */
  #cadUnit() {
    return '/' + this.#cadenceUnit;
  }

  /**
   * Recommended frequency position (1-based) for a variant.
   * new-buybox resolution chain: variant-level metafield → product-level
   * metafield → 1. For every other style the variant level doesn't
   * exist, so this returns the product-level value — callers validate
   * against allocations.length and fall back to the first allocation,
   * which makes the final `1` equivalent to their previous blank-
   * metafield fallback.
   * @param {any | null} variant
   * @returns {number}
   */
  #nbRecommendedPosition(variant) {
    if (this.#nb && variant?.id != null) {
      const rec = Number(this.#nb.variants?.[String(variant.id)]?.recommended);
      if (Number.isFinite(rec) && rec >= 1) return rec;
    }
    const prodRec = Number(this.#context?.recommended_delivery_frequency_option ?? 0);
    if (Number.isFinite(prodRec) && prodRec >= 1) return prodRec;
    return 1;
  }

  /**
   * Replace the visible frequency <select> options with the given
   * allocations. Attributes match what the Liquid renders so price
   * readers can use the same selectors regardless of whether the
   * options were server-rendered or JS-rebuilt.
   *
   * Horizon schema note: allocations carry flat `selling_plan_id` +
   * `selling_plan_group_id`; the plan name / options live in the
   * source product's `selling_plan_groups[].selling_plans[]`. Look up
   * via the planLookup map built from the same product.
   *
   * @param {any} variant
   * @param {any[]} allocations
   * @param {Map<string, {id:number,name:string,group_id:string}>} planLookup
   */
  #rebuildFrequencyOptions(variant, allocations, planLookup) {
    const select = this.#frequenciesSelect;
    if (!select) return;

    const recommended = this.#nbRecommendedPosition(variant);
    const recIdx = recommended <= allocations.length
      ? recommended - 1
      : 0; // Default to first plan when metafield is blank/out-of-range.
    const showRec = this.#nbShowRecommendedFor(variant?.id);

    const frag = document.createDocumentFragment();
    allocations.forEach((alloc, i) => {
      const planId = alloc?.selling_plan_id;
      if (planId == null) return;
      const plan = planLookup.get(String(planId));
      if (!plan) return;

      const o = document.createElement('option');
      o.value = String(planId);
      o.dataset.variantId = String(variant.id);
      o.dataset.sellingPlanId = String(planId);
      if (alloc.price != null) o.dataset.sellingPlanPrice = String(alloc.price);
      if (alloc.compare_at_price != null) {
        o.dataset.sellingPlanCompareAtPrice = String(alloc.compare_at_price);
      }
      const gid = alloc?.selling_plan_group_id ?? plan.group_id;
      if (gid) o.dataset.sellingPlanGroupId = String(gid);

      const adj = alloc.price_adjustments?.[0];
      if (adj) {
        if (adj.value != null) o.dataset.sellingPlanAdjustmentsValue = String(adj.value);
        if (adj.value_type != null) {
          // Liquid emits `data-selling-plan-adjustments-value_type` — preserve the underscore.
          o.setAttribute('data-selling-plan-adjustments-value_type', String(adj.value_type));
        }
      }
      const isRec = i === recIdx;
      if (isRec) o.dataset.recommended = 'true';
      o.textContent = isRec && showRec ? `${plan.name} (Recommended)` : plan.name;
      frag.appendChild(o);
    });

    // Swap atomically so assistive tech sees a single update.
    select.innerHTML = '';
    select.appendChild(frag);
  }

  /** @param {boolean} hidden */
  #setFrequencyWrapperHidden(hidden) {
    const w = this.#frequenciesSelectWrapper;
    if (!w) return;
    if (hidden) {
      w.setAttribute('hidden', '');
    } else {
      w.removeAttribute('hidden');
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Writer: push #selectedSellingPlanId into the hidden input, master
   * select, and URL. Also handles the one-time → empty-plan case.
   * ──────────────────────────────────────────────────────────────── */
  #updateSellingPlanSelectValue() {
    const id = this.#selectedSellingPlanId;

    // Hidden input value — the one thing that matters for cart submission.
    if (this.#sellingPlanInput) {
      this.#sellingPlanInput.value = id != null ? String(id) : '';
    }

    // Master select: mark the matching <option> (by both variant id
    // and selling plan id) selected. For one-time, select the empty
    // sentinel option (value="", data-selling-plan-id="onetime").
    this.#syncMasterSelect(id);

    // URL sync.
    this.#updateSellingPlanParam(id);
  }

  /** @param {number | null} id */
  #syncMasterSelect(id) {
    const master = this.#masterSellingPlanSelect;
    if (!master) return;

    if (id == null) {
      // One-time: select the sentinel <option value="" data-selling-plan-id="onetime">.
      for (const o of master.options) {
        if (o.dataset.sellingPlanId === 'onetime') {
          o.selected = true;
          return;
        }
      }
      master.selectedIndex = 0;
      return;
    }

    // Prefer the option whose variant id matches the target variant
    // for the current mode — avoids picking the main-product option
    // when the user is in a bundle mode (same plan id could exist on
    // both products in theory; match both axes to disambiguate).
    const effMode = this.#effectiveMode();
    const targetVariant = effMode ? this.#targetVariantFor(effMode) : null;
    const targetVid = targetVariant ? String(targetVariant.id) : null;
    const sid = String(id);

    let fallback = null;
    for (const o of master.options) {
      if (o.dataset.sellingPlanId === sid) {
        if (!targetVid || o.dataset.variantId === targetVid) {
          o.selected = true;
          return;
        }
        fallback ??= o;
      }
    }
    if (fallback) fallback.selected = true;
  }

  /** @param {number | null} id */
  #updateSellingPlanParam(id) {
    const url = new URL(window.location.href);
    if (id == null) {
      url.searchParams.delete('selling_plan');
    } else {
      url.searchParams.set('selling_plan', String(id));
    }
    // replaceState (not pushState) — matches variant-picker.js pattern;
    // subscription changes shouldn't pollute history.
    window.history.replaceState({}, '', url.toString());
  }

  /* ────────────────────────────────────────────────────────────────
   * Price updaters
   *
   * Every card shows a live price regardless of which radio is
   * checked, so the user can compare values before committing.
   *   - Display price = the allocation that WOULD be used if the
   *     user picked that card (selected plan if mode matches, else
   *     recommended / first allocation on that card's target variant).
   *   - Compare-at span = variant.compare_at_price (MSRP). Shown
   *     only when compare_at_price > variant.price — matches the
   *     server-render gate in Liquid so JS and SSR stay in agreement.
   *
   * Each card's markup is regenerated client-side to match
   * `snippets/subscription-price.liquid` byte-for-byte so the visual
   * structure (dollars + optional <sup>cents</sup>) is consistent
   * across transitions.
   * ──────────────────────────────────────────────────────────────── */
  #updatePrices() {
    this.#updateOnetimePrices();
    this.#updateSubscriptionPrices();
    this.#updateAdditionalSubscriptionPrices();
    this.#updateSixMonthsSubscriptionPrices();
    this.#updateBuyOncePrice();
  }

  #updateOnetimePrices() {
    const variant = this.#twoProducts ? this.#activeVariant() : this.#mainVariant();
    if (!variant) return;
    const onetimeSlot = this.querySelector('[data-onetime-price]');

    // Two-product new-buybox: per-unit one-time (price ÷ box-count) with the
    // unit suffix, unless onetime_show_full_price is set. No compare-at, matching
    // Expanse. Single-product new-buybox falls through to the full-price path
    // below (unchanged — only quantity-on-onetime differs, handled elsewhere).
    if (this.#nb && this.#twoProducts) {
      if (this.#nbOnetimeFullFor(variant.id)) {
        this.#renderPrice(onetimeSlot, variant.price);
      } else {
        this.#renderPrice(
          onetimeSlot,
          this.#nbPerUnit(variant.price, variant.id),
          '/' + this.#nbUnitFor(variant.id)
        );
      }
      const cmp = this.querySelector('[data-onetime-compare-at-price]');
      if (cmp) {
        cmp.setAttribute('hidden', '');
        cmp.innerHTML = '';
      }
      return;
    }

    this.#renderPrice(onetimeSlot, variant.price);
    this.#renderCompareAt(
      this.querySelector('[data-onetime-compare-at-price]'),
      variant.compare_at_price,
      variant.price
    );
  }

  #updateSubscriptionPrices() {
    // Two-product: the shared S&S card shows the ACTIVE product's subscription
    // price. subMode is fixed to that product's subscribe mode (independent of
    // the onetime/subscription radio) so the card always previews the sub price.
    const variant = this.#twoProducts ? this.#activeVariant() : this.#mainVariant();
    if (!variant) return;
    const subMode = (this.#twoProducts && this.#activeBuyboxProduct === 'additional')
      ? 'additional_subscription'
      : 'subscription';
    const alloc = this.#pickDisplayAllocation(subMode, variant);
    const displayPrice = alloc?.price ?? variant.price;

    if (this.#cadenceActive) {
      // cadence pricing: monthly allocation price as-is (months = 1) with
      // the /month unit; compare = the MAIN variant's one-time price as
      // the per-month baseline, struck WITH the unit (auto-hidden by
      // #renderCompareAt when ≤ the sub price). Liquid paints the same
      // shape on first render — keep in lockstep.
      const unit = this.#cadUnit();
      this.#renderPrice(
        this.querySelector('[data-subscription-price]'),
        displayPrice,
        unit
      );
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        variant.price,
        displayPrice,
        unit
      );
      return;
    }

    if (this.#nb) {
      // new-buybox per-unit price. Single-product keeps whole-dollar rounding
      // (#nbPerUnit), two-product keeps cents (per-bottle). Unit word + compare
      // (universal 1-unit base price) follow the active product; compare hidden
      // when ≤ the per-unit price or when base_price is null.
      const perBox = this.#nbPerUnit(displayPrice, variant.id);
      const unit = '/' + this.#nbUnitFor(variant.id);
      this.#renderPrice(
        this.querySelector('[data-subscription-price]'),
        perBox,
        unit
      );
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        this.#nb.base_price,
        perBox,
        unit
      );
      return;
    }

    this.#renderPrice(
      this.querySelector('[data-subscription-price]'),
      displayPrice
    );
    if (this.#pricingBoxStyle === 'v4' || this.#pricingBoxStyle === 'v5') {
      // v4/v5: compare subscription (allocation) price against the regular
      // one-time price so the strikethrough always shows the full price.
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        variant.price,   // compareAtCents = regular price
        displayPrice     // gateCents = allocation/subscription price
      );
    } else {
      // Compare-at gate uses variant.price (MSRP vs regular), not the
      // allocation price — matches Liquid's `compare_at > variant.price`
      // guard. Subscription's allocation discount is ADDITIONAL on top.
      this.#renderCompareAt(
        this.querySelector('[data-subscription-compare-at-price]'),
        variant.compare_at_price,
        variant.price
      );
    }
  }

  #updateAdditionalSubscriptionPrices() {
    if (!this.#additionalProduct) return;
    const variant = this.#targetVariantFor('additional_subscription');
    if (!variant) return;
    const alloc = this.#pickDisplayAllocation('additional_subscription', variant);
    let displayPrice = alloc?.price ?? variant.price;
    // Cadence is checked FIRST and returns, so its ÷3 can never stack on top of
    // v3's — the Liquid arm this mirrors is an `elsif`. (Only reachable if a v3
    // product ever sets show_cadence_pricing; the styles are meant to be
    // exclusive, and this keeps them so.)
    if (this.#cadenceActive) {
      // cadence pricing: per-month price (÷ months, cents-precision round —
      // matches the Liquid `times: 1.0 | divided_by: 3 | round` arm; keep
      // in lockstep) + /month unit. Compare = the MAIN product's one-time
      // price as the per-month baseline, NOT the bundle's own compare_at.
      displayPrice = Math.round(displayPrice / CADENCE_MONTHS.additional_subscription);
      const unit = this.#cadUnit();
      this.#renderPrice(
        this.querySelector('[data-additional-subscription-price]'),
        displayPrice,
        unit
      );
      this.#renderCompareAt(
        this.querySelector('[data-additional-subscription-compare-at-price]'),
        this.#mainVariant()?.price,
        displayPrice,
        unit
      );
      return;
    }
    if (this.#pricingBoxStyle === 'v3') displayPrice = Math.round(displayPrice / 3);
    this.#renderPrice(
      this.querySelector('[data-additional-subscription-price]'),
      displayPrice
    );
    this.#renderCompareAt(
      this.querySelector('[data-additional-subscription-compare-at-price]'),
      variant.compare_at_price,
      variant.price
    );
  }

  #updateSixMonthsSubscriptionPrices() {
    if (!this.#sixMonthsProduct) return;
    const variant = this.#targetVariantFor('additional_six_months_subscription');
    if (!variant) return;
    const alloc = this.#pickDisplayAllocation(
      'additional_six_months_subscription',
      variant
    );
    let displayPrice = alloc?.price ?? variant.price;
    if (this.#cadenceActive) {
      // cadence pricing: per-month price (÷ months, cents-precision round —
      // matches the Liquid `times: 1.0 | divided_by: 6 | round` arm; keep
      // in lockstep) + /month unit. Compare = the MAIN product's one-time
      // price as the per-month baseline, NOT the bundle's own compare_at.
      displayPrice = Math.round(
        displayPrice / CADENCE_MONTHS.additional_six_months_subscription
      );
      const unit = this.#cadUnit();
      this.#renderPrice(
        this.querySelector('[data-additional-six-months-subscription-price]'),
        displayPrice,
        unit
      );
      this.#renderCompareAt(
        this.querySelector('[data-additional-six-months-subscription-compare-at-price]'),
        this.#mainVariant()?.price,
        displayPrice,
        unit
      );
      return;
    }
    this.#renderPrice(
      this.querySelector('[data-additional-six-months-subscription-price]'),
      displayPrice
    );
    this.#renderCompareAt(
      this.querySelector('[data-additional-six-months-subscription-compare-at-price]'),
      variant.compare_at_price,
      variant.price
    );
  }

  /* ────────────────────────────────────────────────────────────────
   * v5 — "Buy once for $X" link
   *
   * Rendered inside <subscription-options> by Liquid, then moved by
   * JS to after the ATC button so it sits below the primary CTA.
   * The wrapper is found by [data-buy-once-section] (section-scoped)
   * so it survives being outside the custom element's own subtree.
   * ──────────────────────────────────────────────────────────────── */

  /** @returns {HTMLElement | null} */
  #findBuyOnceWrapper() {
    if (!this.#sectionId) return null;
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-buy-once-section="${this.#sectionId}"]`)
    );
  }

  /**
   * Move the buy-once wrapper to after the ATC submit button's
   * block-level container. Safe to call multiple times — skips if
   * the wrapper is already outside this element.
   */
  #placeBuyOnce() {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    // Already placed outside — nothing to do.
    if (!this.contains(wrapper)) return;

    const submitBtn =
      this.#form?.querySelector('button[type="submit"]') ??
      this.#form?.querySelector('input[type="submit"]');
    if (!submitBtn) return;

    const myParent = this.parentElement;
    if (!myParent) return;

    // Walk up from submit button to find the ancestor that shares
    // our parent — i.e. the sibling block that contains the ATC button.
    let ancestor = submitBtn.parentElement;
    while (ancestor && ancestor.parentElement !== myParent) {
      ancestor = ancestor.parentElement;
    }
    (ancestor ?? submitBtn.parentElement)?.after(wrapper);
  }

  /**
   * Update the price text inside the buy-once button.
   * Skips if the button is in success/loading state (disabled).
   */
  #updateBuyOncePrice() {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-buy-once-btn]'));
    if (btn?.disabled) return;
    const priceEl = wrapper.querySelector('[data-buy-once-price]');
    if (!priceEl) return;
    const variant = this.#mainVariant();
    if (!variant) return;
    // Format as dollars-only (no cents) to match the compact link style.
    const formatted = this.#formatMoney(variant.price).trim();
    const idx = formatted.lastIndexOf('.');
    const dollars = idx === -1 ? formatted : formatted.slice(0, idx);
    priceEl.innerHTML = `<span class="subscription-option__price-dollars">${this.#escape(dollars)}</span>`;
  }

  /**
   * Wire up the buy-once click handler and place the wrapper.
   * Called from connectedCallback and updatedCallback.
   */
  #initBuyOnce() {
    // v5 ("Buy once for $X") and cadence-buybox ("One Time Purchase - $X")
    // share the whole buy-once machinery; only the Liquid prefix differs.
    if (this.#pricingBoxStyle !== 'v5' && this.#pricingBoxStyle !== 'cadence-buybox') return;
    this.#placeBuyOnce();
    this.#updateBuyOncePrice();
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = wrapper.querySelector('[data-buy-once-btn]');
    if (!btn || btn.dataset.buyOnceListenerAttached) return;
    btn.dataset.buyOnceListenerAttached = 'true';
    this.#buyOnceClickHandler = (e) => this.#handleBuyOnceClick(e);
    btn.addEventListener('click', this.#buyOnceClickHandler);
  }

  /** @param {Event} _e */
  async #handleBuyOnceClick(_e) {
    const wrapper = this.#findBuyOnceWrapper();
    if (!wrapper) return;
    const btn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-buy-once-btn]'));
    const msgEl = wrapper.querySelector('[data-buy-once-msg]');
    if (!btn) return;

    const variant = this.#mainVariant();
    if (!variant) return;

    // Save original markup so we can restore it on error.
    const originalHTML = btn.innerHTML;

    btn.disabled = true;
    btn.classList.add('subscription-options__buy-once-btn--loading');
    btn.innerHTML = 'Adding…';
    if (msgEl) msgEl.hidden = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description ?? data.message ?? 'Could not add to cart.');
      }

      btn.classList.remove('subscription-options__buy-once-btn--loading');
      btn.textContent = 'Product added to cart';

      // Fetch updated cart and dispatch Horizon's cart:update event so
      // the cart icon count and cart drawer both reflect the new item.
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = cartRes.ok ? await cartRes.json() : null;
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, data: {} },
          })
        );
      } catch (_) {
        // Cart refresh failure is non-critical — item was already added.
      }
    } catch (err) {
      btn.disabled = false;
      btn.classList.remove('subscription-options__buy-once-btn--loading');
      btn.innerHTML = originalHTML;
      if (msgEl) {
        msgEl.hidden = false;
        msgEl.textContent =
          err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        msgEl.className =
          'subscription-options__buy-once-msg subscription-options__buy-once-msg--error';
      }
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * ATC button price display
   *
   * Injects/updates a `[data-subscription-atc-price]` span inside the
   * submit button's `.add-to-cart-text__content` area so the user always
   * sees the price they're about to pay on the button itself.
   *
   * Called on mount, updatedCallback (morph), radio change, frequency
   * change, and variant update — i.e. every moment the price could change.
   *
   * The span is injected by JS so no Liquid changes to add-to-cart-button
   * are needed. The buy-buttons morph on variant:update recreates the
   * button, but updatedCallback re-runs this method and re-injects.
   * ──────────────────────────────────────────────────────────────── */
  #applyAtcPrice() {
    const mode = this.#effectiveMode();

    // Resolve the price for the currently-selected option.
    let priceInCents = null;
    switch (mode) {
      case 'onetime': {
        const v = this.#mainVariant();
        priceInCents = v?.price ?? null;
        break;
      }
      case 'subscription': {
        const v = this.#mainVariant();
        if (v) {
          const alloc = this.#pickDisplayAllocation('subscription', v);
          priceInCents = alloc?.price ?? v.price ?? null;
        }
        break;
      }
      case 'additional_subscription': {
        const v = this.#targetVariantFor('additional_subscription');
        if (v) {
          const alloc = this.#pickDisplayAllocation('additional_subscription', v);
          priceInCents = alloc?.price ?? v.price ?? null;
        }
        break;
      }
      case 'additional_onetime': {
        // One-time purchase of the additional product: full variant price, no plan.
        const v = this.#targetVariantFor('additional_onetime');
        priceInCents = v?.price ?? null;
        break;
      }
      case 'additional_six_months_subscription': {
        const v = this.#targetVariantFor('additional_six_months_subscription');
        if (v) {
          const alloc = this.#pickDisplayAllocation('additional_six_months_subscription', v);
          priceInCents = alloc?.price ?? v.price ?? null;
        }
        break;
      }
      default:
        break;
    }

    // Locate the submit button inside the product form.
    const button = /** @type {HTMLButtonElement | null} */ (
      this.#form?.querySelector('button[type="submit"][name="add"]') ?? null
    );
    if (!button) return;

    const textContent = button.querySelector('.add-to-cart-text__content');
    if (!textContent) return;

    // Find or create the price slot.
    let slot = /** @type {HTMLElement | null} */ (
      textContent.querySelector('[data-subscription-atc-price]')
    );
    if (!slot) {
      slot = document.createElement('span');
      slot.setAttribute('data-subscription-atc-price', '');
      textContent.appendChild(slot);
    }

    // new-buybox (spec §3.7): active total through #priceMarkup
    // (superscript non-zero cents, '.00' dropped — consistent with
    // the cards) + struck `base_price × boxes` compare sibling,
    // removed when ≤ the active total (equal prices = no strike,
    // e.g. one-time on the 1-box variant).
    if (this.#nb) {
      if (priceInCents != null) {
        slot.innerHTML = this.#priceMarkup(priceInCents);
      } else {
        slot.textContent = '';
      }

      let compare = /** @type {HTMLElement | null} */ (
        textContent.querySelector('[data-subscription-atc-compare]')
      );
      const boxes = this.#nbBoxesFor(this.#twoProducts ? this.#activeVariantId() : this.#currentMainVariantId);
      const baseCents = this.#nb.base_price;
      const compareCents = baseCents != null ? baseCents * boxes : null;
      if (
        priceInCents != null &&
        compareCents != null &&
        compareCents > priceInCents
      ) {
        if (!compare) {
          compare = document.createElement('span');
          compare.setAttribute('data-subscription-atc-compare', '');
          textContent.appendChild(compare);
        }
        compare.innerHTML = this.#priceMarkup(compareCents);
      } else if (compare) {
        compare.remove();
      }
      return;
    }

    // cadence-buybox: mode TOTAL through #priceMarkup ('.00' dropped) +
    // struck main-one-time × months compare sibling — the same
    // [data-subscription-atc-compare] injection pattern as new-buybox.
    // Monthly: $79 vs $99×1; 3-month: $207 (full bundle price from the
    // mode switch above) vs $99×3. Removed when not strictly greater.
    if (this.#pricingBoxStyle === 'cadence-buybox') {
      if (priceInCents != null) {
        slot.innerHTML = this.#priceMarkup(priceInCents);
      } else {
        slot.textContent = '';
      }

      let compare = /** @type {HTMLElement | null} */ (
        textContent.querySelector('[data-subscription-atc-compare]')
      );
      const months = (mode != null && CADENCE_MONTHS[mode]) || 1;
      const base = this.#mainVariant()?.price ?? null;
      const compareCents = base != null ? base * months : null;
      if (
        priceInCents != null &&
        compareCents != null &&
        compareCents > priceInCents
      ) {
        if (!compare) {
          compare = document.createElement('span');
          compare.setAttribute('data-subscription-atc-compare', '');
          textContent.appendChild(compare);
        }
        compare.innerHTML = this.#priceMarkup(compareCents);
      } else if (compare) {
        compare.remove();
      }
      return;
    }

    // Write the formatted price, or clear when no price is resolved.
    // Strip trailing ".00" so whole-dollar amounts display as "$99"
    // rather than "$99.00". Non-zero cents (e.g. "$9.99") are kept.
    if (priceInCents != null) {
      const formatted = this.#formatMoney(priceInCents).trim();
      const dotIdx = formatted.lastIndexOf('.');
      const centsStr = dotIdx !== -1 ? formatted.slice(dotIdx + 1) : '';
      slot.textContent = centsStr === '00'
        ? formatted.slice(0, dotIdx)
        : formatted;
    } else {
      slot.textContent = '';
    }
  }

  /**
   * Resolve the main product's currently-active variant.
   * Mirrors the logic inside #targetVariantFor('subscription') but
   * exposed as a small accessor so both the onetime card (which has
   * no allocations) and the subscription card (which does) can share
   * the lookup.
   * @returns {any | null}
   */
  #mainVariant() {
    if (!this.#mainProduct) return null;
    return (
      this.#mainProduct.variants.find(
        (v) => Number(v.id) === this.#currentMainVariantId
      ) ??
      this.#mainProduct.variants[0] ??
      null
    );
  }

  /**
   * Pick the allocation to display in a card's price slot.
   *
   *   1. If the user has explicitly selected a plan AND the card's
   *      mode matches the selected mode → use the selected plan
   *      (so the dropdown's choice is reflected immediately).
   *   2. Else → recommended-position allocation (1-indexed) if valid.
   *   3. Else → first allocation.
   *
   * @param {SubOption} mode
   * @param {any} variant
   * @returns {any | null}
   */
  #pickDisplayAllocation(mode, variant) {
    const allocs = variant?.selling_plan_allocations ?? [];
    if (allocs.length === 0) return null;

    if (this.#effectiveMode() === mode && this.#selectedSellingPlanId != null) {
      const match = allocs.find(
        (a) => Number(a?.selling_plan_id) === this.#selectedSellingPlanId
      );
      if (match) return match;
    }

    const rec = this.#nbRecommendedPosition(variant);
    if (rec <= allocs.length) {
      return allocs[rec - 1];
    }

    return allocs[0];
  }

  /**
   * Render a formatted price into the card's main price span.
   * Output shape matches `snippets/subscription-price.liquid`:
   *   v2/v2b → <span class="subscription-option__price-dollars">$99<sup class="subscription-option__price-cents">.00</sup></span>
   *   else   → <span class="subscription-option__price-dollars">$99</span>
   *
   * @param {Element | null} span
   * @param {number | null | undefined} priceInCents
   * @param {string} [unit] Optional unit suffix, e.g. '/box' (new-buybox).
   */
  #renderPrice(span, priceInCents, unit = '') {
    if (!span) return;
    if (priceInCents == null) {
      span.innerHTML = '';
      return;
    }
    span.innerHTML = this.#priceMarkup(priceInCents, unit);
  }

  /**
   * Render or hide the compare-at span.
   *
   * Shown only when `compareAtCents > gateCents` (MSRP strictly
   * greater than the variant's regular price). Otherwise the span
   * gets the `hidden` attribute + empty markup so CSS + AT both
   * skip it cleanly.
   *
   * @param {Element | null} span
   * @param {number | null | undefined} compareAtCents
   * @param {number | null | undefined} gateCents
   * @param {string} [unit] Optional unit suffix, e.g. '/box' (new-buybox).
   */
  #renderCompareAt(span, compareAtCents, gateCents, unit = '') {
    if (!span) return;
    if (
      compareAtCents == null ||
      gateCents == null ||
      compareAtCents <= gateCents
    ) {
      span.setAttribute('hidden', '');
      span.innerHTML = '';
      return;
    }
    span.removeAttribute('hidden');
    span.innerHTML = this.#priceMarkup(compareAtCents, unit);
  }

  /**
   * Produce the inner markup for a price span at the given amount.
   * Shared by #renderPrice and #renderCompareAt so the two stay in
   * lockstep with the Liquid renderer.
   *
   * Uses Horizon's `formatMoney` with the shop's money_format +
   * currency (threaded through the context JSON), then splits on
   * the decimal separator exactly the way the Liquid filter chain
   * (`price | money | split: '.'`) does.
   *
   * new-buybox (spec §3.8): '.00' cents are dropped entirely; non-zero
   * cents render as the same superscript as v2. An optional `unit`
   * suffix (e.g. '/box') renders as a SIBLING span after the dollars
   * span — the Liquid card markup emits
   * `{% render 'subscription-price' %}<span class="subscription-option__price-unit">/box</span>`;
   * keep the two in lockstep.
   *
   * @param {number} priceInCents
   * @param {string} [unit] Optional unit suffix, e.g. '/box'.
   * @returns {string}
   */
  #priceMarkup(priceInCents, unit = '') {
    const formatted = this.#formatMoney(priceInCents).trim();
    // Split at the LAST dot so formats that use "," for decimals
    // and "." for thousands (e.g. de-DE) don't mis-split.
    // Shopify's `money` filter always uses the locale-appropriate
    // separator; the Liquid snippet's `split: '.'` implicitly
    // assumes "." is the decimal, which holds for USD/en-US. We
    // prefer the slightly safer lastIndexOf('.') while matching
    // the USD output byte-for-byte.
    const idx = formatted.lastIndexOf('.');
    const dollars = idx === -1 ? formatted : formatted.slice(0, idx);
    const cents = idx === -1 ? '' : formatted.slice(idx + 1);

    const unitMarkup = unit
      ? `<span class="subscription-option__price-unit">${this.#escape(unit)}</span>`
      : '';

    if (this.#pricingBoxStyle === 'new-buybox' || this.#pricingBoxStyle === 'cadence-buybox') {
      const sup =
        cents !== '' && cents !== '00'
          ? `<sup class="subscription-option__price-cents">.${this.#escape(cents)}</sup>`
          : '';
      return `<span class="subscription-option__price-dollars">${this.#escape(dollars)}${sup}</span>${unitMarkup}`;
    }

    // These two branches append unitMarkup as well, so a style outside the
    // nb/cadence pair can still carry a "/month" suffix (v5 + cadence pricing).
    // Every other caller passes no unit, leaving the output byte-identical.
    if ((this.#pricingBoxStyle === 'v2' || this.#pricingBoxStyle === 'v2b' || this.#pricingBoxStyle === 'v4' || this.#pricingBoxStyle === 'v5') && cents !== '') {
      return (
        `<span class="subscription-option__price-dollars">${this.#escape(dollars)}` +
        `<sup class="subscription-option__price-cents">.${this.#escape(cents)}</sup>` +
        `</span>${unitMarkup}`
      );
    }
    return `<span class="subscription-option__price-dollars">${this.#escape(dollars)}</span>${unitMarkup}`;
  }

  /**
   * @param {number} priceInCents
   * @returns {string}
   */
  #formatMoney(priceInCents) {
    const format = this.#context?.money_format || '${{amount}}';
    const currency = this.#context?.currency || 'USD';
    try {
      return formatMoney(priceInCents, format, currency);
    } catch {
      // Extremely defensive fallback — never throw inside a price
      // render path. USD-shaped output so the UI stays intact.
      const whole = Math.floor(priceInCents / 100);
      const rem = String(Math.abs(priceInCents) % 100).padStart(2, '0');
      return `$${whole}.${rem}`;
    }
  }

  /** Tiny HTML escaper for money strings (no DOM deps). */
  #escape(s) {
    return String(s)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }

  /* ────────────────────────────────────────────────────────────────
   * Capture-phase submit listener
   *
   * For bundle modes (3-mo / 6-mo), the <input name="id"> in the form
   * normally holds the MAIN product's variant ID (managed by
   * product-form-component). We swap it to the BUNDLE product's
   * matched variant ID at submit time so /cart/add.js receives the
   * correct product.
   *
   * Capture phase (third arg `true`) fires before product-form-component's
   * bubble-phase listener, so our override is in place when FormData is
   * built. We never call preventDefault — the form submits normally.
   *
   * Attached at document level (not on the form element) so it fires
   * before product-form-component's own capture-phase listener, which
   * lives on the component element (a DOM ancestor of the form). In
   * capture phase, document fires before any element-level listener,
   * guaranteeing our override is in place when product-form-component
   * reads `new FormData(form)`. Only bound once; the handler checks
   * `e.target === this.#form` to filter to our form only.
   * ──────────────────────────────────────────────────────────────── */

  #bindSubmitListener() {
    // Only bind once — document listener is stable across morphs.
    if (this.#submitHandler) return;

    const handler = (/** @type {SubmitEvent} */ e) => {
      // Ignore submits from other forms on the page (e.g. search, newsletter).
      if (e.target !== this.#form) return;
      this.#onFormSubmit(e);
    };
    document.addEventListener('submit', handler, { capture: true });
    this.#submitHandler = handler;
  }

  #unbindSubmitListener() {
    if (this.#submitHandler) {
      document.removeEventListener('submit', this.#submitHandler, { capture: true });
      this.#submitHandler = null;
    }
  }

  /** @param {SubmitEvent} _e */
  #onFormSubmit(_e) {
    const mode = this.#effectiveMode();
    // Only override for additional-product modes — main subscription and main
    // one-time already use the correct main-product variant ID. additional_onetime
    // also submits the additional product's variant (with no selling plan).
    if (
      mode !== 'additional_subscription' &&
      mode !== 'additional_onetime' &&
      mode !== 'additional_six_months_subscription'
    ) return;

    const bundleVariant = this.#targetVariantFor(mode);
    if (!bundleVariant) return;

    // Overwrite [name="id"] with the bundle variant ID right before
    // product-form-component serializes the form.
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="id"]') ?? null
    );
    if (variantInput) {
      variantInput.value = String(bundleVariant.id);
    }

    // Belt-and-suspenders: assert selling_plan value too, in case
    // updatedCallback morph cleared it between selection and submit.
    if (this.#sellingPlanInput && this.#selectedSellingPlanId != null) {
      this.#sellingPlanInput.value = String(this.#selectedSellingPlanId);
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Proactive variant-ID override
   *
   * Primary mechanism for bundle cart submissions. We keep [name="id"]
   * set to the correct variant at all times:
   *   bundle mode  → bundle variant ID (so product-form-component's
   *                  FormData already has the right value on first click)
   *   other modes  → main product variant ID (restore after leaving bundle)
   * Called on every radio change, variant change, and morph so the
   * value is always current.
   * ──────────────────────────────────────────────────────────────── */
  #applyVariantIdOverride() {
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="id"]') ?? null
    );
    if (!variantInput) return;

    const mode = this.#effectiveMode();
    if (
      mode === 'additional_subscription' ||
      mode === 'additional_onetime' ||
      mode === 'additional_six_months_subscription'
    ) {
      const bundleVariant = this.#targetVariantFor(mode);
      if (bundleVariant) variantInput.value = String(bundleVariant.id);
    } else if (this.#currentMainVariantId != null) {
      // Switching away from bundle mode — restore to main product variant
      // so one-time and monthly submissions use the correct product.
      variantInput.value = String(this.#currentMainVariantId);
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Quantity-selector visibility
   *
   * Subscription products on Recharge always charge for a fixed
   * quantity (the quantity-selector value is meaningless because the
   * recurring order is built off the selling-plan, not a per-cycle
   * quantity input). Hide the selector for any subscription mode and
   * show it for one-time only.
   *
   * Re-queried each call because morph after variant:update may have
   * replaced the <quantity-selector-component> element under our
   * cached pointer.
   * ──────────────────────────────────────────────────────────────── */
  /* ────────────────────────────────────────────────────────────────
   * Per-variant quantity label (Slim Sips)
   *
   * The block renders one <span data-variant-quantity-label="<id>">
   * per variant inside the subscription card, with `hidden` on every
   * span except the current variant's. This keeps the label in sync
   * with #currentMainVariantId on every state change.
   *
   * No-ops on any PDP that didn't render the spans (i.e. anything
   * other than the Slim Sips template) — zero regression risk.
   * ──────────────────────────────────────────────────────────────── */
  #applyQuantityLabel() {
    const spans = this.querySelectorAll('[data-variant-quantity-label]');
    if (!spans.length) return;
    // Two-product: follow the ACTIVE product's variant (additional bundle
    // variant when the additional tile is selected); otherwise the main variant.
    const currentId = this.#twoProducts ? this.#activeVariantId() : this.#currentMainVariantId;
    spans.forEach((span) => {
      span.hidden = Number(span.dataset.variantQuantityLabel) !== currentId;
    });
  }

  /* ────────────────────────────────────────────────────────────────
   * Per-variant modal body (new-buybox, spec §3.6)
   *
   * The subscription info modal carries one
   * <div data-variant-modal-body="<id>"> per variant; only the
   * current variant's is visible. #applyQuantityLabel can't reach
   * these — #portalModals moves the modal to <body>, outside `this` —
   * so this companion queries the portalled copy (by data-portal-for)
   * AND any not-yet-portalled copy still inside the component (the
   * first call in connectedCallback runs before #portalModals; the
   * server-rendered hidden state is already correct at that point).
   *
   * No-ops at zero nodes — every non-new-buybox PDP.
   * ──────────────────────────────────────────────────────────────── */
  #applyVariantModalBody() {
    const sectionId = this.#sectionId ?? '';
    const nodes = [
      ...document.querySelectorAll(
        `[data-subscription-popup][data-portal-for="${sectionId}"] [data-variant-modal-body]`
      ),
      ...this.querySelectorAll('[data-variant-modal-body]'),
    ];
    if (!nodes.length) return;
    const currentId = this.#twoProducts ? this.#activeVariantId() : this.#currentMainVariantId;
    nodes.forEach((node) => {
      node.hidden = Number(node.dataset.variantModalBody) !== currentId;
    });
  }

  #applyQuantityVisibility() {
    const productDetails = this.closest('.product-details');
    // Target the wrapper div rather than the inner component so that hiding it
    // removes it as a flex item entirely — no phantom gap before the ATC button.
    // Re-queried each call in case morph after variant:update replaced the element.
    const wrapper = /** @type {HTMLElement | null} */ (
      productDetails?.querySelector('.quantity-selector-wrapper') ?? null
    );
    this.#quantitySelector = /** @type {HTMLElement | null} */ (
      wrapper?.querySelector('quantity-selector-component') ?? null
    );
    if (!wrapper) return;

    // new-buybox is a curated box — quantity is fixed at 1 for EVERY mode
    // (incl. one-time), matching Expanse. All other styles keep the original
    // rule: hide for subscription modes, show for one-time.
    const hide = this.#nb
      ? true
      : this.#selectedOption != null && this.#selectedOption !== 'onetime';
    wrapper.style.display = hide ? 'none' : '';

    // Reset quantity to 1 when switching to any subscription mode.
    // Recharge subscriptions are always quantity 1; leaving a user-set
    // value of e.g. 3 would silently add 3 subscription items to cart.
    if (hide) {
      const qtyInput = /** @type {HTMLInputElement | null} */ (
        this.#quantitySelector?.querySelector('input[name="quantity"]') ?? null
      );
      if (qtyInput && qtyInput.value !== '1') qtyInput.value = '1';
    }
  }

  /* ────────────────────────────────────────────────────────────────
   * Info-modal show/hide
   *
   * Modal markup lives inside this block (rendered by
   * snippets/subscription-options-modal.liquid) and starts with the
   * `hidden` attribute. Toggling the attribute is sufficient — the
   * block's own CSS guards visibility off `[hidden]` so we don't have
   * to manage display: none inline.
   * ──────────────────────────────────────────────────────────────── */

  /**
   * Move all [data-subscription-popup] elements to <body> so they
   * escape the `position: sticky; z-index: 1` stacking context of
   * .product-details (.sticky-content--desktop).  Without this, the
   * fixed overlay is confined to that stacking context and loses to
   * the header (z-index: 8) in the global stack.
   *
   * Each modal is tagged with data-portal-for="<sectionId>" so:
   *  - #openModal / #closeAllOpenModals can scope queries per instance
   *  - disconnectedCallback can clean them up
   *  - updatedCallback can replace them after a morph re-creates them
   */
  #portalModals() {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    // Remove previously portalled copies (morph re-creates them inside
    // the component — drop the stale body copies before moving fresh ones).
    document
      .querySelectorAll(`[data-subscription-popup][data-portal-for="${sectionId}"]`)
      .forEach((m) => m.remove());

    // Move current modals from inside this element to <body>.
    this.querySelectorAll('[data-subscription-popup]').forEach((m) => {
      m.setAttribute('data-portal-for', sectionId);
      document.body.appendChild(m);
    });
  }

  /** @param {string} id */
  #openModal(id) {
    // Close any other open modal first so two info popups can't
    // visually overlap if a user rapid-clicks two triggers.
    this.#closeAllOpenModals();
    const sectionId = this.#sectionId ?? '';
    // Prefer the portalled copy in <body>; fall back to inside `this`
    // in case #portalModals hasn't run yet (e.g. during unit tests).
    const modal =
      document.querySelector(
        `[data-subscription-popup="${id}"][data-portal-for="${sectionId}"]`
      ) ?? this.querySelector(`[data-subscription-popup="${id}"]`);
    if (!modal) return;
    modal.removeAttribute('hidden');
  }

  /** @param {Element} modal */
  #closeModal(modal) {
    modal.setAttribute('hidden', '');
  }

  #closeAllOpenModals() {
    const sectionId = this.#sectionId ?? '';
    document
      .querySelectorAll(
        `[data-subscription-popup]:not([hidden])[data-portal-for="${sectionId}"]`
      )
      .forEach((m) => m.setAttribute('hidden', ''));
    // Also close any that weren't yet portalled (safety net).
    this.querySelectorAll('[data-subscription-popup]:not([hidden])').forEach((m) =>
      m.setAttribute('hidden', '')
    );
  }
}

if (!customElements.get('subscription-options')) {
  customElements.define('subscription-options', SubscriptionOptions);
}
